import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { Box, IconButton, Paper } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import HighlightIcon from "@mui/icons-material/Highlight";

// R1-UI-014 — clickable inline annotation for a movement's directive text.
//
// Tokens (whitespace-separated) cycle through:
//   plain → bold → underline → inverse (circled) → highlight → plain
//
// The persisted form is the markdown subset documented in R1-PLAN-002
// (see packages/score-common/src/schemas/validation.js):
//   bold       → **word**
//   underline  → <u>word</u>
//   inverse    → <span class="circled">word</span>
//   highlight  → <mark>word</mark>
//
// #687 — highlight added as a 4th mark alongside bold/underline/circled, so
// a judge can flag a word/phrase the way a highlighter pen would, distinct
// from underline/circle/bold. Same tap-to-cycle mechanism, no new UI.
//
// If `annotated` is empty/null we derive tokens from `plain`. Once any
// token is annotated, the annotated string becomes the source of truth.
//
// #2079 — a scribe can also select ("highlight") a run of words by
// dragging (mouse) or touch-dragging across them, then pick one mark from a
// small toolbar to apply it to the whole selection in one action, instead
// of tapping every word individually. This is a second, additive way to
// reach the exact same states/markdown (see onRangeApply below) — the
// per-word tap-to-cycle keeps working unchanged (AC3). Card 2080 (next in
// this workstream) adds a control that clears all annotations on a field in
// one action; that hook belongs beside the field's onChange caller
// (ScoringForm.jsx), not inside this shared renderer.

const STATES = ["plain", "bold", "underline", "inverse", "highlight"];

// #580 — a pipe in the directive/description source is a segment separator
// from FVD import data (e.g. "Halt|Salute"); render it as " - " rather than
// leaking a literal "|" token. Unlike AnnotatableText (which splits on "|" to
// stack lines), the directives column is single-line, so the pipe is joined
// inline. Annotated markdown never carries pipes (serializeTokens joins with
// spaces), so normalising both the plain and annotated paths is safe.
export function normalizePipes(s) {
  return (s ?? "").replaceAll("|", " - ");
}

export function parseAnnotated(s) {
  const tokens = [];
  let i = 0;
  while (i < s.length) {
    // Skip whitespace.
    const ws = s.slice(i).match(/^\s+/);
    if (ws) {
      i += ws[0].length;
      continue;
    }
    let m;
    if ((m = s.slice(i).match(/^\*\*([^*]+)\*\*/))) {
      tokens.push({ text: m[1], state: "bold" });
      i += m[0].length;
    } else if ((m = s.slice(i).match(/^<u>([^<]+)<\/u>/))) {
      tokens.push({ text: m[1], state: "underline" });
      i += m[0].length;
    } else if (
      (m = s.slice(i).match(/^<span class="circled">([^<]+)<\/span>/))
    ) {
      tokens.push({ text: m[1], state: "inverse" });
      i += m[0].length;
    } else if ((m = s.slice(i).match(/^<mark>([^<]+)<\/mark>/))) {
      tokens.push({ text: m[1], state: "highlight" });
      i += m[0].length;
    } else if ((m = s.slice(i).match(/^\S+/))) {
      tokens.push({ text: m[0], state: "plain" });
      i += m[0].length;
    } else {
      // Defensive — unparseable run, advance one char to avoid an
      // infinite loop.
      i++;
    }
  }
  return tokens;
}

export function serializeTokens(tokens) {
  return tokens
    .map(({ text, state }) => {
      switch (state) {
        case "bold":
          return `**${text}**`;
        case "underline":
          return `<u>${text}</u>`;
        case "inverse":
          return `<span class="circled">${text}</span>`;
        case "highlight":
          return `<mark>${text}</mark>`;
        default:
          return text;
      }
    })
    .join(" ");
}

function nextState(s) {
  return STATES[(STATES.indexOf(s) + 1) % STATES.length];
}

// #308 — collapse consecutive same-state, non-plain tokens into runs so
// visually adjacent words with matching characteristics render as one
// contiguous selection (continuous underline / a single circle / one bold
// run) instead of separate marks. Plain tokens stay singleton runs. Each
// item keeps its global token index so a word remains individually
// clickable (and un-markable) within its group.
export function groupRuns(tokens) {
  const runs = [];
  tokens.forEach((tok, idx) => {
    const last = runs[runs.length - 1];
    if (last && last.state === tok.state && tok.state !== "plain") {
      last.items.push({ tok, idx });
    } else {
      runs.push({ state: tok.state, items: [{ tok, idx }] });
    }
  });
  return runs;
}

// #687 — opaque highlighter yellow with a forced high-contrast dark text
// color, rather than `currentColor`/a theme token. A translucent highlight
// would read fine against a light scoresheet but wash out (or clash with)
// light-on-dark body text in dark mode; an opaque marker + fixed dark text
// looks like an actual highlighter pen and stays legible in both themes.
const HIGHLIGHT_BG = "#fff59d";
const HIGHLIGHT_TEXT = "rgba(0, 0, 0, 0.87)";

function styleFor(state) {
  switch (state) {
    case "bold":
      return { fontWeight: 700 };
    case "underline":
      return { textDecoration: "underline" };
    case "inverse":
      // "circle" / inverse marker — outlined oval around the word.
      return {
        display: "inline-block",
        border: 1.5,
        borderColor: "currentColor",
        borderRadius: 999,
        px: 0.75,
        py: 0,
        lineHeight: 1.2,
      };
    case "highlight":
      return {
        display: "inline-block",
        backgroundColor: HIGHLIGHT_BG,
        color: HIGHLIGHT_TEXT,
        borderRadius: 0.5,
        px: 0.25,
        py: 0,
      };
    default:
      return undefined;
  }
}

// #2079 — the four marks offered by the range-select toolbar, in the same
// order as the tap-cycle (minus "plain": a range select is for adding a
// mark, not clearing one — clearing a single word is still a tap away, and
// clearing everything is card 2080's job).
const RANGE_MARKS = [
  { state: "bold", label: "Bold selection", Icon: FormatBoldIcon },
  { state: "underline", label: "Underline selection", Icon: FormatUnderlinedIcon },
  { state: "inverse", label: "Circle selection", Icon: RadioButtonUncheckedIcon },
  { state: "highlight", label: "Highlight selection", Icon: HighlightIcon },
];

// Shared renderer for an array of annotation tokens. Groups adjacent
// same-state words into one contiguous selection (#308) while keeping each
// word individually clickable to cycle its state. Used by both
// DirectiveAnnotation (directives) and AnnotatableText (pipe-aware
// descriptions / collective names) so the grouping behaves identically
// everywhere a judge marks words.
//
// #2079 — `onRangeApply(startIdx, endIdx, state)` is optional; when given,
// dragging (mouse) or touch-dragging across two or more words shows a small
// toolbar to apply one mark to the whole span in one action. A plain tap
// (no movement) never touches this path — it still fires the word's own
// onClick exactly as before (AC3).
export function AnnotatedTokens({ tokens, disabled, onTokenClick, onRangeApply, sx }) {
  const runs = useMemo(() => groupRuns(tokens), [tokens]);
  const containerRef = useRef(null);
  const dragRef = useRef(null); // { startIdx, lastIdx } while a drag is in flight
  const toolbarRef = useRef(null);
  const [dragPreview, setDragPreview] = useState(null); // { start, end } live, while dragging
  const [pendingRange, setPendingRange] = useState(null); // { start, end, rect } after release

  const rangeEnabled = Boolean(onRangeApply) && !disabled;

  useEffect(() => {
    if (!rangeEnabled) return undefined;

    const idxFromPoint = (x, y) => {
      const el = document.elementFromPoint(x, y);
      const target = el?.closest?.("[data-token-idx]");
      if (!target || !containerRef.current?.contains(target)) return null;
      const idx = Number(target.dataset.tokenIdx);
      return Number.isNaN(idx) ? null : idx;
    };

    const handleMove = (e) => {
      if (!dragRef.current) return;
      dragRef.current.lastX = e.clientX;
      dragRef.current.lastY = e.clientY;
      const idx = idxFromPoint(e.clientX, e.clientY);
      if (idx == null) return;
      dragRef.current.lastIdx = idx;
      setDragPreview({
        start: Math.min(dragRef.current.startIdx, idx),
        end: Math.max(dragRef.current.startIdx, idx),
      });
    };

    // Builds the pending-toolbar range from a completed (startIdx, lastIdx)
    // span. Shared by a genuine pointerup and a pointercancel this file
    // decides to treat as a completed along-the-row drag (see handleCancel).
    const commitRange = (startIdx, lastIdx) => {
      // A drag that never left its starting word is a plain tap — leave it
      // to the word's own onClick (tap-to-cycle), don't open the toolbar.
      if (startIdx === lastIdx) return;
      const start = Math.min(startIdx, lastIdx);
      const end = Math.max(startIdx, lastIdx);
      const startEl = containerRef.current?.querySelector(
        `[data-token-idx="${start}"]`,
      );
      const endEl = containerRef.current?.querySelector(
        `[data-token-idx="${end}"]`,
      );
      const rect =
        startEl && endEl
          ? {
              top: Math.min(
                startEl.getBoundingClientRect().top,
                endEl.getBoundingClientRect().top,
              ),
              left: Math.min(
                startEl.getBoundingClientRect().left,
                endEl.getBoundingClientRect().left,
              ),
            }
          : { top: 0, left: 0 };
      setPendingRange({ start, end, rect });
    };

    const handleUp = () => {
      if (!dragRef.current) return;
      const { startIdx, lastIdx } = dragRef.current;
      dragRef.current = null;
      setDragPreview(null);
      commitRange(startIdx, lastIdx);
    };

    // #2085 — pointercancel fires for two different real situations, not
    // just the accidental one: a vertical thumb-scroll that drifts across a
    // token boundary under #2079's `touchAction: pan-y` (the spurious-
    // toolbar defect this card fixes), AND a genuine along-the-row
    // drag-select, because Chrome hands a touch to the scroll compositor
    // (and cancels the pointer) whenever the row's ancestor pane is
    // vertically scrollable, even when the actual movement is horizontal —
    // confirmed empirically: the real-touch CDP spec below drives an
    // intentional two-word horizontal swipe and it too ends in
    // pointercancel, never pointerup. Event type alone can't tell these
    // apart, so key off the gesture's dominant axis instead: our tokens
    // read along one line, so a real drag-select is horizontal-dominant;
    // an accidental scroll drift is vertical-dominant. Only the latter gets
    // discarded outright — the former still opens the toolbar (AC1/AC2/AC4).
    const handleCancel = () => {
      if (!dragRef.current) return;
      const { startIdx, lastIdx, startX, startY, lastX, lastY } = dragRef.current;
      dragRef.current = null;
      setDragPreview(null);
      const dx = Math.abs((lastX ?? startX) - startX);
      const dy = Math.abs((lastY ?? startY) - startY);
      if (dx < dy) return; // vertical-dominant: a scroll drift, discard it
      commitRange(startIdx, lastIdx);
    };

    document.addEventListener("pointermove", handleMove);
    document.addEventListener("pointerup", handleUp);
    document.addEventListener("pointercancel", handleCancel);
    return () => {
      document.removeEventListener("pointermove", handleMove);
      document.removeEventListener("pointerup", handleUp);
      document.removeEventListener("pointercancel", handleCancel);
    };
  }, [rangeEnabled]);

  // Dismiss the pending toolbar on the next pointerdown outside it (picking
  // a mark, or starting a fresh drag, both count as "outside").
  useEffect(() => {
    if (!pendingRange) return undefined;
    const handleOutside = (e) => {
      if (toolbarRef.current && !toolbarRef.current.contains(e.target)) {
        setPendingRange(null);
      }
    };
    document.addEventListener("pointerdown", handleOutside);
    return () => document.removeEventListener("pointerdown", handleOutside);
  }, [pendingRange]);

  const applyRangeState = (state) => {
    if (!pendingRange) return;
    onRangeApply(pendingRange.start, pendingRange.end, state);
    setPendingRange(null);
  };

  // A single clickable word. `styled` applies the run's visual treatment
  // to the word itself (bold/underline single words, and so the existing
  // per-word style assertions hold); the inverse group oval is applied to
  // the run wrapper instead, with its words left plain.
  const word = (tok, idx, styled, state) => {
    const inDragPreview =
      dragPreview && idx >= dragPreview.start && idx <= dragPreview.end;
    return (
      <Box
        component="span"
        data-token-idx={idx}
        role={disabled ? undefined : "button"}
        // #668 — annotation is a mouse/stylus gesture, not a keyboard one
        // (same rationale as #535's IconButtons). tabIndex={-1} keeps the
        // word out of the Tab sequence while leaving it focusable by click,
        // so a click-then-Enter/Space still cycles its state.
        tabIndex={disabled ? undefined : -1}
        onClick={disabled ? undefined : () => onTokenClick(idx)}
        onKeyDown={
          disabled
            ? undefined
            : (e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onTokenClick(idx);
                }
              }
        }
        // #2079 — pointerdown seeds a possible drag-select; it never calls
        // preventDefault, so the browser's own click still fires right
        // after for a plain tap, leaving tap-to-cycle untouched.
        onPointerDown={
          rangeEnabled
            ? (e) => {
                // #2085 — startX/startY seed the dominant-axis check
                // handleCancel uses to tell a genuine along-the-row drag
                // from an accidental vertical scroll drift.
                dragRef.current = {
                  startIdx: idx,
                  lastIdx: idx,
                  startX: e.clientX,
                  startY: e.clientY,
                  lastX: e.clientX,
                  lastY: e.clientY,
                };
              }
            : undefined
        }
        sx={{
          cursor: disabled ? "default" : "pointer",
          userSelect: "none",
          whiteSpace: "nowrap",
          // #2079 — "pan-y", not "none": our drag-select gesture reads
          // words along one line (horizontal), so only horizontal panning
          // needs to be ours to arbitrate. "none" would turn every word on
          // a kiosk scoring sheet into a vertical-scroll dead zone, which a
          // scribe's thumb lands on constantly. "pan-y" leaves the browser
          // free to treat a mostly-vertical drag as a normal page scroll
          // (our pointermove listener still runs regardless, it just loses
          // the race to the browser's own scroll for a vertical gesture).
          touchAction: rangeEnabled ? "pan-y" : undefined,
          "&:hover": disabled
            ? undefined
            : { backgroundColor: "action.hover", borderRadius: 0.5 },
          ...(inDragPreview
            ? { backgroundColor: "action.selected", borderRadius: 0.5 }
            : undefined),
          ...(styled ? styleFor(state) : undefined),
        }}
      >
        {tok.text}
      </Box>
    );
  };

  const renderRun = ({ state, items }, ri) => {
    // A multi-word circled run gets ONE oval around the whole group; the
    // words inside stay plain so it reads as a single contiguous mark.
    if (state === "inverse" && items.length > 1) {
      return (
        <Box component="span" sx={styleFor("inverse")} key={ri}>
          {items.map(({ tok, idx }, k) => (
            <Fragment key={idx}>
              {word(tok, idx, false, state)}
              {k < items.length - 1 ? " " : ""}
            </Fragment>
          ))}
        </Box>
      );
    }
    // Plain words, single inverse words, and bold/underline runs: each word
    // carries its own style. For a same-state run the connector space is
    // styled too (e.g. an underlined space) so the selection is visually
    // contiguous across the gap rather than broken at each word.
    return (
      <Fragment key={ri}>
        {items.map(({ tok, idx }, k) => (
          <Fragment key={idx}>
            {word(tok, idx, state !== "plain", state)}
            {k < items.length - 1 ? (
              <Box component="span" sx={styleFor(state)}>
                {" "}
              </Box>
            ) : (
              ""
            )}
          </Fragment>
        ))}
      </Fragment>
    );
  };

  return (
    <Box component="span" ref={containerRef} sx={{ display: "inline", ...sx }}>
      {/* #221 — render a real space between runs (not a margin) so the text
          has word-boundary wrap points and never breaks mid-word. */}
      {runs.map((run, ri) => (
        <Fragment key={ri}>
          {renderRun(run, ri)}
          {ri < runs.length - 1 ? " " : ""}
        </Fragment>
      ))}
      {/* #2079 — a floating toolbar appears once a drag-select spans two or
          more words, offering the four marks to apply to the whole span in
          one action. Positioned near the top-left of the selection. */}
      {pendingRange && (
        <Paper
          ref={toolbarRef}
          elevation={4}
          data-testid="annotation-range-toolbar"
          sx={{
            position: "fixed",
            top: Math.max(pendingRange.rect.top - 44, 4),
            left: pendingRange.rect.left,
            zIndex: 1300,
            display: "flex",
            p: 0.25,
            gap: 0.25,
          }}
        >
          {RANGE_MARKS.map(({ state, label, Icon }) => (
            <IconButton
              key={state}
              size="small"
              aria-label={label}
              data-testid={`annotation-range-${state}`}
              onClick={() => applyRangeState(state)}
            >
              <Icon fontSize="small" />
            </IconButton>
          ))}
        </Paper>
      )}
    </Box>
  );
}

export default function DirectiveAnnotation({
  plain,
  annotated,
  disabled,
  onChange,
}) {
  const tokens = useMemo(
    () =>
      parseAnnotated(
        normalizePipes(annotated && annotated.length > 0 ? annotated : plain ?? ""),
      ),
    [plain, annotated]
  );

  const handleTokenClick = (idx) => {
    const next = tokens.map((tok, i) =>
      i === idx ? { ...tok, state: nextState(tok.state) } : tok
    );
    onChange?.(serializeTokens(next));
  };

  // #2079 — apply one mark to every token in [startIdx, endIdx], from the
  // range-select toolbar. Same tokens/serializeTokens path as a tap, so it
  // persists and reopens identically to any other annotation (AC2).
  const handleRangeApply = (startIdx, endIdx, state) => {
    const next = tokens.map((tok, i) =>
      i >= startIdx && i <= endIdx ? { ...tok, state } : tok
    );
    onChange?.(serializeTokens(next));
  };

  return (
    <AnnotatedTokens
      tokens={tokens}
      disabled={disabled}
      onTokenClick={handleTokenClick}
      onRangeApply={onChange ? handleRangeApply : undefined}
      sx={{ color: "text.secondary", fontSize: "0.75rem" }}
    />
  );
}
